import { useTranslation } from 'react-i18next';

export const useDateTimeFormat = () => {
  const { t, i18n } = useTranslation();

  /**
   * 日時の JST 構成要素を返す内部ヘルパー
   * Intl.DateTimeFormat + timeZone:'Asia/Tokyo' を使用するため、
   * 端末のタイムゾーン設定に依存しない。
   */
  const getJstParts = (date: Date) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      weekday: 'short',
      hour12: false,
    }).formatToParts(date);
    const get = (type: string) =>
      parts.find((p) => p.type === type)?.value ?? '0';
    const weekdayMap: Record<string, number> = {
      Sun: 0,
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
    };
    return {
      year: parseInt(get('year'), 10),
      month: parseInt(get('month'), 10),
      day: parseInt(get('day'), 10),
      // hour12:false 環境によっては '24' が返るため % 24 で正規化
      hour: parseInt(get('hour'), 10) % 24,
      minute: parseInt(get('minute'), 10),
      weekdayIndex: weekdayMap[get('weekday')] ?? 0,
    };
  };

  /**
   * 時刻を24時間形式の文字列でフォーマットする（HH:MM）
   * @param date - Dateオブジェクト
   * @returns HH:MM形式の時刻文字列
   */
  const format24HourTime = (date: Date): string => {
    const { hour, minute } = getJstParts(date);
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  /**
   * 時間を12時間形式でフォーマットする
   * @param date - Dateオブジェクト
   * @param lowercase - am/pmを小文字にするかどうか（デフォルト: true）
   * @returns 12時間形式の時間文字列（例: "8:00pm", "11:30am"）
   */
  const format12Hour = (date: Date, lowercase = true): string => {
    const { hour, minute } = getJstParts(date);
    const hour12 = hour % 12 || 12;
    const period = hour >= 12 ? 'pm' : 'am';
    return `${hour12}:${minute.toString().padStart(2, '0')}${lowercase ? period : period.toUpperCase()}`;
  };

  /**
   * 日付を言語に応じてフォーマットする
   * @param dateString - ISO形式の日付文字列
   * @returns フォーマットされた日付文字列
   * @example
   *   日本語: "2025年7月11日"
   *   英語: "July 11, 2025"
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-US' : 'ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  /**
   * 時間を"HH:MM"形式でフォーマットする（言語に応じてAM/PM形式も対応）
   * @param timeString - ISO形式の日時文字列
   * @returns 言語に応じた時間文字列
   * @example
   *   日本語: "23:00"
   *   英語: "11:00 PM"
   */
  const formatTime = (timeString: string): string => {
    const date = new Date(timeString);
    const { hour, minute } = getJstParts(date);

    if (i18n.language === 'en') {
      const period = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
    }

    return format24HourTime(date);
  };

  /**
   * 日付を曜日付きでフォーマットする
   * @param dateString - ISO形式の日付文字列
   * @returns 言語に応じてフォーマットされた日付文字列
   * @example
   *   日本語: "2025年7月11日（金）"
   *   英語: "July 11, 2025 (Fri)"
   */
  const formatDateWithWeekday = (dateString: string): string => {
    const date = new Date(dateString);
    const weekdayKeys = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];
    const { year, month, day, weekdayIndex } = getJstParts(date);
    const weekday = t(`common.weekdays.${weekdayKeys[weekdayIndex]}`);

    if (i18n.language === 'en') {
      // 英語: "Friday, July 11, 2025"
      const fullWeekday = t(`common.dayOfWeek.${weekdayKeys[weekdayIndex]}`);
      const formattedDate = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(date);
      return `${fullWeekday}, ${formattedDate}`;
    }

    return `${year}年${month}月${day}日（${weekday}）`;
  };

  /**
   * 日付を曜日付きでフォーマットする（予約ページ用・曜日フルネーム）
   * @param dateString - ISO形式の日付文字列
   * @returns 言語に応じてフォーマットされた日付文字列
   * @example
   *   日本語: "2025年7月11日（金）"
   *   英語: "Friday, July 11, 2025"
   */
  const formatDateWithWeekdayLong = (dateString: string): string => {
    const date = new Date(dateString);
    const weekdayKeys = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];
    const { year, month, day, weekdayIndex } = getJstParts(date);
    const weekday = t(`common.weekdays.${weekdayKeys[weekdayIndex]}`);

    if (i18n.language === 'en') {
      const formattedDate = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Tokyo',
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(date);
      return formattedDate;
    }

    return `${year}年${month}月${day}日（${weekday}）`;
  };

  /**
   * 予約時間の範囲をフォーマットする
   * @param startTime - ISO形式の開始時刻文字列
   * @param endTime - ISO形式の終了時刻文字列
   * @returns 言語に応じてフォーマットされた時間範囲文字列
   * @example
   *   日本語: "2025年7月11日 23:00～23:30"
   *   英語: "July 11, 2025 23:00-23:30"
   */
  const formatReservationTimeRange = (
    startTime: string,
    endTime: string,
  ): string => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const { year, month, day } = getJstParts(start);

    if (i18n.language === 'en') {
      const dateFormatted = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(start);
      return `${dateFormatted} ${format24HourTime(start)}-${format24HourTime(end)}`;
    }

    return `${year}年${month}月${day}日 ${format24HourTime(start)}～${format24HourTime(end)}`;
  };

  /**
   * 日時から終了時刻を計算し、予約時間範囲をフォーマットする
   * @param startTime - ISO形式の開始時刻文字列
   * @param durationMinutes - 利用時間（分）
   * @returns 言語に応じてフォーマットされた時間範囲文字列
   * @example
   *   日本語: "2025年7月11日 23:00～23:30"
   *   英語: "July 11, 2025 from 8:00pm to 8:30pm"
   */
  const formatReservationTimeWithDuration = (
    startTime: string,
    durationMinutes: number,
  ): string => {
    const start = new Date(startTime);
    const end = new Date(start.getTime() + durationMinutes * 60 * 1000);

    if (i18n.language === 'en') {
      const dateFormatted = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(start);
      return `${dateFormatted} from ${format12Hour(start)} to ${format12Hour(end)}`;
    }

    return formatReservationTimeRange(start.toISOString(), end.toISOString());
  };

  /**
   * 日時をYYYY/MM/DD hh:mm~形式でフォーマットする
   * @param datetime - ISO形式の日時文字列
   * @returns YYYY/MM/DD hh:mm~形式の日時文字列
   * @example "2025/11/10 10:00~"
   */
  const formatDateTimeSlash = (datetime: string): string => {
    const date = new Date(datetime);
    const { year, month, day, hour, minute } = getJstParts(date);
    return `${year}/${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')} ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}~`;
  };

  /**
   * 日付を「空き状況」付きでフォーマットする
   * @param dateString - YYYY-MM-DD形式の日付文字列
   * @returns 言語に応じてフォーマットされた日付文字列
   */
  const formatDateWithAvailability = (dateString: string): string => {
    const date = new Date(dateString);
    const weekdayKeys = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];
    const { year, month, day, weekdayIndex } = getJstParts(date);
    const weekday =
      i18n.language === 'en'
        ? t(`common.dayOfWeek.${weekdayKeys[weekdayIndex]}`)
        : t(`common.weekdays.${weekdayKeys[weekdayIndex]}`);

    const formattedMonth =
      i18n.language === 'en'
        ? new Intl.DateTimeFormat('en-US', {
            timeZone: 'Asia/Tokyo',
            month: 'long',
          }).format(date)
        : month;

    const formattedDate = t('common.dateFormat.dateWithWeekday', {
      year,
      month: formattedMonth,
      day,
      weekday,
    });

    return t('common.dateFormat.availabilityTitle', { date: formattedDate });
  };

  /**
   * 予約履歴用の時間範囲フォーマット
   * @param startTime - ISO形式の開始時刻文字列
   * @param endTime - ISO形式の終了時刻文字列
   * @returns フォーマットされた時間範囲文字列
   * @example
   *   日本語: "2025/11/10 10:00 ~ 10:30"
   *   英語: "November 10, 2025 10:00 AM ~ 10:30 AM"
   */
  const formatReservationHistoryTimeRange = (
    startTime: string,
    endTime: string,
  ): string => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const {
      year,
      month,
      day,
      hour: startHour,
      minute: startMinute,
    } = getJstParts(start);
    const { hour: endHour, minute: endMinute } = getJstParts(end);

    if (i18n.language === 'en') {
      const dateFormatted = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(start);
      const startPeriod = startHour >= 12 ? 'PM' : 'AM';
      const endPeriod = endHour >= 12 ? 'PM' : 'AM';
      const startHour12 = startHour % 12 || 12;
      const endHour12 = endHour % 12 || 12;
      const startMinuteStr = startMinute.toString().padStart(2, '0');
      const endMinuteStr = endMinute.toString().padStart(2, '0');
      return `${dateFormatted} ${startHour12.toString().padStart(2, '0')}:${startMinuteStr} ${startPeriod} ~ ${endHour12.toString().padStart(2, '0')}:${endMinuteStr} ${endPeriod}`;
    }

    return `${year}/${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')} ${format24HourTime(start)} ~ ${format24HourTime(end)}`;
  };

  /**
   * 日付をスラッシュ形式でフォーマットする（予約履歴用）
   * @param dateString - ISO形式の日付文字列
   * @returns フォーマットされた日付文字列
   * @example
   *   日本語: "2025/7/11"
   *   英語: "Dec 20, 2025"
   */
  const formatHistoryDate = (dateString: string): string => {
    const date = new Date(dateString);
    const { year, month, day } = getJstParts(date);

    if (i18n.language === 'en') {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
    }

    return `${year}/${month}/${day}`;
  };

  /**
   * 時間範囲をフォーマットする（予約履歴用）
   * @param startTime - ISO形式の開始時刻文字列
   * @param endTime - ISO形式の終了時刻文字列
   * @returns フォーマットされた時間範囲文字列
   * @example
   *   日本語: "13:00〜13:30"
   *   英語: "1:00 PM - 1:30 PM"
   */
  const formatHistoryTimeRange = (
    startTime: string,
    endTime: string,
  ): string => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const { hour: startHour, minute: startMinute } = getJstParts(start);
    const { hour: endHour, minute: endMinute } = getJstParts(end);

    if (i18n.language === 'en') {
      const startHour12 = startHour % 12 || 12;
      const endHour12 = endHour % 12 || 12;
      const startMinuteStr = startMinute.toString().padStart(2, '0');
      const endMinuteStr = endMinute.toString().padStart(2, '0');
      const startPeriod = startHour >= 12 ? 'PM' : 'AM';
      const endPeriod = endHour >= 12 ? 'PM' : 'AM';
      return `${startHour12}:${startMinuteStr} ${startPeriod} - ${endHour12}:${endMinuteStr} ${endPeriod}`;
    }

    return `${format24HourTime(start)}〜${format24HourTime(end)}`;
  };

  /**
   * スタッフ専用メニューでの予約時間の範囲をフォーマットする
   * @param startTime - ISO形式の開始時刻文字列
   * @param endTime - ISO形式の終了時刻文字列
   * @returns 言語に応じてフォーマットされた時間範囲文字列
   * @example
   *   日本語: "2025年7月11日（金） 23:00～23:30"
   */
  const formatReservationTimeRangeStaffOnly = (
    startTime: string,
    endTime: string,
  ): [string, string] => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const { year, month, day } = getJstParts(start);

    const timeRange =
      i18n.language === 'en'
        ? `${format24HourTime(start)}-${format24HourTime(end)}`
        : `${format24HourTime(start)}～${format24HourTime(end)}`;

    if (i18n.language === 'en') {
      const dateFormatted = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Tokyo',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'short',
      }).format(start);
      return [dateFormatted, timeRange];
    }

    const weekday = new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      weekday: 'short',
    }).format(start);
    return [`${year}年${month}月${day}日(${weekday})`, timeRange];
  };

  /**
   * 時間（24時間形式）を言語に応じてフォーマットする
   * @param hour - 0-23の時間
   * @param lowercase - 英語のAM/PMを小文字にするかどうか（デフォルト: false）
   * @returns 言語に応じた時間ラベル
   * @example
   *   日本語: "21時"
   *   英語: "9:00 PM" (lowercase=false) or "9:00 pm" (lowercase=true)
   */
  const formatHourLabel = (hour: number, lowercase = false): string => {
    if (i18n.language === 'en') {
      const period = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:00 ${lowercase ? period.toLowerCase() : period}`;
    }

    return `${hour}${t('common.hourSuffix')}`;
  };

  /**
   * 時間範囲と所要時間を言語に応じてフォーマットする
   * @param startTime - ISO形式の開始時刻文字列
   * @param endTime - ISO形式の終了時刻文字列
   * @param durationMinutes - 所要時間（分）
   * @returns 言語に応じた時間範囲と所要時間の文字列
   * @example
   * 日本語: "23:00 – 23:30（30分）"
   * 英語: "11:00pm to 11:30pm (30 minutes)"
   */
  const formatTimeRangeWithDuration = (
    startTime: string,
    endTime: string,
    durationMinutes: number,
  ): string => {
    const start = new Date(startTime);
    const end = new Date(endTime);

    if (i18n.language === 'en') {
      const startFormatted = format12Hour(start);
      const endFormatted = format12Hour(end);
      return `${startFormatted} to ${endFormatted} (${durationMinutes} minutes)`;
    }

    return `${format24HourTime(start)}${'〜'}${format24HourTime(end)}（${durationMinutes}${'分'}）`;
  };

  /**
   * 時刻をJST（日本時間）固定でフォーマットする
   * Intl.DateTimeFormat の timeZone: 'Asia/Tokyo' を使用して、
   * 端末のタイムゾーン設定に関わらず常にJSTで表示する
   * @param timeString - ISO8601形式の日時文字列（例: "2025-12-09T00:30:00+09:00"）
   * @returns 言語に応じた時刻文字列
   * @example
   *   日本語: "00:30"
   *   英語: "12:30 AM"
   */
  const formatTimeJst = (timeString: string): string => {
    if (!timeString) return '';

    const date = new Date(timeString);
    if (isNaN(date.getTime())) return '';

    if (i18n.language === 'en') {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Tokyo',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).format(date);
    }

    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  };

  /**
   * JST固定の時間範囲フォーマット（予約履歴用）
   * @param startTime - ISO8601形式の開始時刻文字列
   * @param endTime - ISO8601形式の終了時刻文字列
   * @returns フォーマットされた時間範囲文字列
   * @example
   *   日本語: "00:30〜01:00"
   *   英語: "12:30 AM - 1:00 AM"
   */
  const formatHistoryTimeRangeJst = (
    startTime: string,
    endTime: string,
  ): string => {
    const startFormatted = formatTimeJst(startTime);
    const endFormatted = formatTimeJst(endTime);

    if (i18n.language === 'en') {
      return `${startFormatted} - ${endFormatted}`;
    }
    return `${startFormatted}〜${endFormatted}`;
  };

  return {
    formatDate,
    formatTime,
    formatHourLabel,
    formatDateWithWeekday,
    formatDateWithWeekdayLong,
    formatReservationTimeRange,
    formatReservationTimeWithDuration,
    formatTimeRangeWithDuration,
    formatDateTimeSlash,
    formatDateWithAvailability,
    formatReservationHistoryTimeRange,
    formatHistoryDate,
    formatHistoryTimeRange,
    formatReservationTimeRangeStaffOnly,
    formatTimeJst,
    formatHistoryTimeRangeJst,
  };
};
